import { AlertTriangle, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Falha ao carregar uma lista/painel. Existe para a UI não confundir erro com
 * vazio: antes, uma query que falhava caía no `EmptyState` ("em breve",
 * "R$ 0") e escondia o problema. O pai decide quando mostrar (`isError`).
 */
export interface LoadErrorStateProps {
  title?: string;
  description?: string;
  /** Normalmente o `refetch` da query. Sem ele, não há botão. */
  onRetry?: () => void;
  size?: "sm" | "md";
  className?: string;
}

export function LoadErrorState({
  title = "Não foi possível carregar",
  description = "Verifique sua conexão e tente de novo.",
  onRetry,
  size = "md",
  className,
}: LoadErrorStateProps) {
  const retry = onRetry && (
    <Button type="button" variant="outline" size="sm" onClick={onRetry}>
      <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
      Tentar de novo
    </Button>
  );

  if (size === "sm") {
    return (
      <div
        role="alert"
        className={cn(
          "flex flex-wrap items-center justify-center gap-3 py-6 text-sm text-muted-foreground",
          className,
        )}
      >
        <span>{title}</span>
        {retry}
      </div>
    );
  }

  return (
    <div
      role="alert"
      className={cn("flex flex-col items-center justify-center px-6 py-10 text-center", className)}
    >
      <span
        aria-hidden="true"
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive"
      >
        <AlertTriangle className="h-5 w-5" />
      </span>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">{description}</p>
      {retry && <div className="mt-4">{retry}</div>}
    </div>
  );
}
